import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Formik } from 'formik';
import screen from 'superior-mq';
import handleData from 'Lib/handleData';
import { bp } from 'Styles/helpers';
import { Router } from '../../../routes';
import Container from '../../container';
import Button from '../../button';
import GullSelect from '../../forms/gull-select';
import Input from '../../forms/input';
import CurrencyInput from '../../forms/currency-input';
import FileUpload from '../../forms/file-upload';
import FormGrid from '../form-grid';
import GullTextArea from '../../forms/gull-text-area';
import Grid from '../../grid';
import Waves from '../../waves';
import Loading from '../../loading';

const MainFormSection = styled.section`
  position: relative;
  padding-bottom: 430px;

  ${screen.below(bp.tablet, `
    padding-bottom: 330px;
  `)}

  ${screen.below(bp.portrait, `
    padding-bottom: 230px;
  `)}
`;

const FormSection = styled.section`
  padding: 120px 0;

  &:last-of-type {
    padding: 0;
  }

  ${screen.below(bp.desktop, `
    padding: 100px 0;
  `)}

  ${screen.below(bp.laptopSm, `
    padding: 80px 0;
  `)}

  ${screen.below(bp.tablet, `
    padding: 60px 0;
  `)}

  ${screen.below(bp.mobile, `
    padding: 40px 0;
  `)}

  ${props => props.styles && css`
    ${props.styles}
  `}
`;

const FieldDescription = styled.p`
  margin-bottom: 40px;

  ${screen.below(bp.mobile, `
    margin-bottom: 20px;
  `)}
`;

const ButtonGridItem = styled(Grid.Item)`
  grid-column: 5 / span 7;

  ${screen.below(bp.portrait, `
    grid-column: 2 / span 10;
  `)}

  ${screen.below(bp.mobileSm, `
    grid-column: span 12;
  `)}
`;

const CreateButton = styled(Button)`
  ${screen.below(bp.portrait, `
    padding: 0 20px;
  `)}
`;

const ErrorState = styled.div`
  color: ${props => props.theme.error};
  font-weight: 600;
  font-size: 14px;
  padding: 10px 0;
`;

const durationOptions = (edit = '') => [
  { value: 0, label: 'Life of Project' },
  { value: 1, label: `${edit}One Month` },
  { value: 2, label: `${edit}Two Months` },
  { value: 3, label: `${edit}Three Months` },
  { value: 6, label: `${edit}Six Months` },
  { value: 12, label: `${edit}1 year` },
];

export default class CreateFundraiserForm extends PureComponent {

  static propTypes = {
    programs: PropTypes.object,
    loggedIn: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    stepContent: PropTypes.array,
  };

  static defaultProps = {
    stepContent: [],
  };

  state = {
    status: null,
  };

  validate = (values) => {
    const { edit } = this.props;
    const errors = {};

    if (!values.program) {
      errors.program = 'Please select a program to support';
    }

    if (!values.title) {
      errors.title = 'Please add a title to your fundraiser';
    }

    if (!values.description) {
      errors.description = 'Please add a description';
    }

    if (!values.goal) {
      errors.goal = 'Please add a donation amount goal';
    } else if (!/(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/.test(values.goal)) {
      errors.goal = 'Please enter a valid a number';
    }

    if (!edit && !values.duration) {
      errors.duration = 'Please select a fundraiser duration';
    }

    return errors;
  }

  // eslint-disable-next-line no-unused-vars
  onSubmit = (values, { setSubmitting }) => {
    const { fundraiser } = this.props;

    this.setState({ status: null });

    const postData = new FormData();

    postData.append('projectType', 3);

    // step 2
    postData.append('projectId', fundraiser ? fundraiser.id : values.program.value);

    // step 3
    postData.append('title', values.title);
    postData.append('description', values.description);
    postData.append('image', values.image);

    // step 4
    if (values.goal) {
      const rawGoal = values.goal.toString().replace(/\$/, '').replace(/,/gm, '');

      postData.append('goal', rawGoal);
    }

    if (values.duration) {
      postData.append('duration', values.duration.value);
    }

    handleData(
      this.props.edit ? 'PUT' : 'POST',
      `/v1/fundraisers${this.props.edit ? `/${this.props.fundraiser.id}` : ''}`,
      postData,
      { 'Content-Type': 'multipart/form-data' },
    )
      .then((response) => {
        setSubmitting(false);
        Router.push(`/fundraisers/${response.slug}`);
      })
      .catch((error) => {
        this.setState({ status: error });
        setSubmitting(false);
      });
  };

  programSelect = () => (
    this.props.programs.items.map(program => ({
      label: program.title,
      value: program.id,
    }))
  );

  render() {
    const { edit, fundraiser } = this.props;
    const [step2, step3, step4] = this.props.stepContent;
    const fields = edit ? fundraiser.fields : null;

    return (
      <MainFormSection>
        <Container>
          <Formik
            initialValues={{
              program: edit
                ?
                  {
                    value: fundraiser.parentProject.id,
                    label: fundraiser.parentProject.title,
                  }
                :
                  {
                    value: 0,
                    label: 'Waves For Water - General Fund',
                  },
              title: edit ? fundraiser.title : '',
              description: edit ? fields.description : '',
              image: '',
              goal: edit ? fields.goal.raw : '',
              duration: '',
            }}
            validate={this.validate}
            onSubmit={this.onSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
            }) => (
              <form onSubmit={handleSubmit}>
                <FormSection>
                  <FormGrid number="02">

                    {step2 && step2.fields &&
                      <Fragment>
                        <h3>{step2.fields.title}</h3>
                        <FieldDescription>
                          {step2.fields.description}
                        </FieldDescription>
                      </Fragment>
                    }

                    <GullSelect
                      options={this.programSelect()}
                      value={values.program}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      errors={errors.program && touched.program && errors.program}
                      touched={touched.program}
                      valueName="program"
                      id="program-select"
                      placeholder="Program To Support"
                      required
                      isDisabled={edit}
                      async
                      dataEndpoint="/v1/projects?type=1"
                    />
                  </FormGrid>
                </FormSection>

                <FormSection>
                  <FormGrid number="03">

                    {step3 && step3.fields &&
                      <Fragment>
                        <h3>{step3.fields.title}</h3>
                        <FieldDescription>
                          {step3.fields.description}
                        </FieldDescription>
                      </Fragment>
                    }

                    <Input
                      type="text"
                      name="title"
                      label="Title"
                      placeholder="Title"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.title}
                      errors={errors.title && touched.title && errors.title}
                      required
                    />
                    <GullTextArea
                      name="description"
                      label="Description"
                      placeholder="Description"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.description}
                      errors={errors.description && touched.description && errors.description}
                      required
                    />
                    <FileUpload
                      name="image"
                      label="Photo"
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      file={values.image}
                      touched={touched.state}
                      errors={errors.image && touched.image && errors.image}
                    />
                  </FormGrid>
                </FormSection>

                <FormSection styles="padding-bottom: 80px">
                  <FormGrid number="04">

                    {step4 && step4.fields &&
                      <Fragment>
                        <h3>{step4.fields.title}</h3>
                        <FieldDescription>
                          {step4.fields.description}
                        </FieldDescription>
                      </Fragment>
                    }

                    <CurrencyInput
                      type="text"
                      name="goal"
                      label="Goal Amount"
                      placeholder="Goal Amount"
                      pattern="[0-9]*"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.goal}
                      errors={errors.goal && touched.goal && errors.goal}
                      required
                    />
                    <GullSelect
                      options={durationOptions(edit ? 'Additional ' : '')}
                      value={values.duration}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      errors={errors.duration && touched.duration && errors.duration}
                      touched={touched.duration}
                      valueName="duration"
                      id="duration"
                      placeholder="Duration"
                      required={!edit}
                    />
                  </FormGrid>
                </FormSection>

                <FormSection>
                  <Grid>
                    <ButtonGridItem>
                      {!isSubmitting &&
                        <CreateButton
                          disabled={!this.props.loggedIn}
                          formNoValidate="formnovalidate"
                          type="submit"
                        >
                          {edit ? 'Update' : 'Create'} Fundraiser
                        </CreateButton>
                      }

                      {isSubmitting &&
                        <Loading fluid align="start" />
                      }

                      {this.state.status &&
                        <ErrorState>
                          {this.state.status}
                        </ErrorState>
                      }
                    </ButtonGridItem>
                  </Grid>
                </FormSection>

              </form>
            )}
          </Formik>
        </Container>
        <Waves
          position="bottom"
          alignment="bottom"
          height={360}
        />
      </MainFormSection>
    );
  }
}
